import { TCreateInvoiceRequestBody, TPreviewInvoicePdfRequestBody, TUpdateInvoiceRequestBody } from '@qapline/core';
import { ELanguage } from '@qapline/translations';
import { InvoiceFormSchema } from 'modules/invoicing/types';

const toNullableNumber = (value: string | null | undefined): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

// All invoice dates travel as unix timestamps in SECONDS.
const nowInSeconds = () => Math.floor(Date.now() / 1000);

const toTimestampString = (flag: boolean, existing: number | null | undefined): string | null => {
    if (flag) {
        const seconds = existing && existing > 0 ? existing : nowInSeconds();
        return String(seconds);
    }
    return null;
};

const toTimestampOrNullString = (value: string | null | undefined): string | null => {
    if (value === null || value === undefined || value === '') return null;
    return String(value);
};

const normalizeVat = (vat: string): number => {
    const n = Number(vat);
    return n === 0 || n === 21 ? n : 21;
};

const mapCommissions = (commissions: InvoiceFormSchema['commissions']) =>
    commissions.map(({ commission_id, vat }) => ({
        commissionId: commission_id,
        vat: normalizeVat(vat),
    }));

type TExistingDates = {
    dateExported?: number | null;
};

export const buildCreateInvoicePayload = (data: InvoiceFormSchema): TCreateInvoiceRequestBody => {
    if (!data.customer_id) {
        throw new Error('customer_id is required to create an invoice');
    }
    if (!data.currency) {
        throw new Error('currency is required to create an invoice');
    }

    return {
        customerId: data.customer_id,
        currency: data.currency,
        commissions: mapCommissions(data.commissions),
        dateOrder: String(Number(data.dateOrder ?? 0)),
        dateIssue: String(Number(data.dateIssue)),
        datePoint: String(Number(data.datePoint)),
        dateDue: String(Number(data.dateDue)),
        dateExported: data.exported ? String(nowInSeconds()) : null,
        constantSymbol: data.constantSymbol ?? null,
        language: data.language as ELanguage,
    };
};

export const buildUpdateInvoicePayload = (
    data: InvoiceFormSchema,
    existingDates: TExistingDates = {},
): TUpdateInvoiceRequestBody => {
    return {
        commissions: mapCommissions(data.commissions),
        dateOrder: String(Number(data.dateOrder ?? 0)),
        dateIssue: String(Number(data.dateIssue)),
        datePoint: String(Number(data.datePoint)),
        dateDue: String(Number(data.dateDue)),
        dateExported: toTimestampString(data.exported, existingDates.dateExported),
        constantSymbol: data.constantSymbol ?? null,
        language: data.language as ELanguage,
        paid: data.paid,
        paidAmount: toNullableNumber(data.paidAmount),
        paymentConfirmationDate: toTimestampOrNullString(data.paymentConfirmationDate),
    };
};

// A date field the user has not filled in yet is left out of the preview payload entirely, so the
// server falls back to the stored invoice or to today instead of rendering the epoch.
const toOptionalTimestamp = (value: string | null | undefined): string | undefined => {
    if (value === null || value === undefined || value === '') return undefined;
    const seconds = Number(value);
    return Number.isFinite(seconds) && seconds > 0 ? String(seconds) : undefined;
};

/**
 * Builds the payload for the live PDF preview. Unlike the create/update payloads this one tolerates
 * a half-filled form: anything still missing is simply omitted and resolved on the server.
 */
export const buildPreviewInvoicePayload = (
    data: InvoiceFormSchema,
    invoiceId?: number,
): TPreviewInvoicePdfRequestBody => ({
    ...(invoiceId ? { invoiceId } : {}),
    ...(data.customer_id ? { customerId: data.customer_id } : {}),
    ...(data.currency ? { currency: data.currency } : {}),
    commissions: mapCommissions(data.commissions),
    dateOrder: toOptionalTimestamp(data.dateOrder),
    dateIssue: toOptionalTimestamp(data.dateIssue),
    datePoint: toOptionalTimestamp(data.datePoint),
    dateDue: toOptionalTimestamp(data.dateDue),
    constantSymbol: data.constantSymbol ?? null,
    language: data.language as ELanguage,
});
